import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { Link, useNavigate, useRouter } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { Fragment, useEffect, useState } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { ButtonStyles } from "@/components/Button/ButtonStyles.ts"
import { Centered } from "@/components/Centered/Centered.ts"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { GitHubPrBadge } from "@/components/GitHubIntegration/GitHubPrBadge.tsx"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useProjectLocation } from "@/hooks/useMatterLocation.ts"
import { useProfile } from "@/hooks/useProfile.ts"
import { DeleteLight, Heart12Gray, Heart12Green, Heart20Green, LinkLight } from "@/icons/Icons.ts"
import { UserInOrgNotFoundException } from "@/lib/client/types.ts"
import { formatTime } from "@/lib/date.ts"
import { formatTaskTitle, pageTitle } from "@/lib/displayUtils.ts"
import { copyToClipboard, normalizeProjectKey, toProfileUsername } from "@/lib/std.ts"
import { Page } from "@/routes/auth/Page.tsx"
import { PAGE_HORIZONTAL_PADDING, PAGE_HORIZONTAL_PADDING_MOBILE } from "@/routes/auth/PageStyles.ts"
import { UserTaskCounts } from "@/routes/auth/UserTaskCounts.tsx"
import { HomeStyles } from "@/routes/home/HomeStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId } from "@/routes/projects/projectHooks.ts"
import { TaskViewSceneStyles } from "@/routes/tasks/TaskListScene/TaskListSceneStyles.ts"
import { useProfileRoute } from "@/routes/tasks/tasksHooks.ts"
import { HeadingStyles } from "@/styles/Markdown.ts"
import { ellipsis, mobileBreakpoint } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { AiAgentTask, GitHubPrInfo } from "@/lib/client/generated/TypeDefinitions.ts"

export const UserProfileScene = observer(() => {
  const orgId = useCurrentOrgId()
  const route = useProfileRoute()
  const navigate = useNavigate()
  const model = route.useLoaderData().model

  useEffect(() => {
    return () => {
      model.terminate()
    }
  }, [model])

  const profile = useProfile(toProfileUsername(model.username))

  const handlePrev = () => {
    void navigate({ to: ".", search: { page: model.page - 1 } })
  }

  const handleNext = () => {
    void navigate({ to: ".", search: { page: model.page + 1 } })
  }

  if (model.isLoading.get()) {
    return (
      <Centered>
        <Spinner text={ellipsize("Loading ideas")} />
      </Centered>
    )
  }

  if (model.fullName.get() == null) {
    throw new UserInOrgNotFoundException(null, orgId)
  }

  return (
    <Page>
      <title>{pageTitle(model.fullName.get())}</title>

      <Centered vertical={false}>
        <HomeStyles.List>
          <PageTitle>
            <EllipsisContainer>
              <UserProfileTitle data-testid="user-profile-name">
                {model.fullName.get()}
              </UserProfileTitle>
            </EllipsisContainer>

            <Spring />

            <SuccessfulToggle
              to="."
              search={{ successful: !model.successful }}
              className={cx(ButtonStyles.Base, ButtonStyles.Kind.Alternative, model.successful ? SuccessfulActive : null)}
            >
              <Horizontal alignItems="center" gap="4px">
                {model.successful ? <Heart12Green /> : <Heart12Gray />}
                Successful
              </Horizontal>
            </SuccessfulToggle>
          </PageTitle>

          {profile.data && <UserTaskCountsContainer><UserTaskCounts profile={profile.data} /></UserTaskCountsContainer>}

          <UserTaskList>
            {model.tasks.get().map(it => (
              <Fragment key={it.date}>
                <HomeStyles.ListTitle className={HeadingStyles.Heading6}>
                  {it.date}
                </HomeStyles.ListTitle>

                {it.tasks.map(task => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    prs={model.gitHubPrs.get().get(task.id)}
                    deleteItem={model.deleteTask}
                  />
                ))}
              </Fragment>
            ))}

            {model.hasMore.get() && (
              <Horizontal key="paging">
                {model.hasPrev.get() && (
                  <Button kind="alternative" onClick={handlePrev}>
                    Previous
                  </Button>
                )}

                <Spring />

                {model.hasNext.get() && (
                  <Button kind="alternative" onClick={handleNext}>
                    Next
                  </Button>
                )}
              </Horizontal>
            )}
          </UserTaskList>
        </HomeStyles.List>

        <FadeOutGradient />
      </Centered>
    </Page>
  )
})

const TaskItem = (props: {
  task: AiAgentTask
  prs: GitHubPrInfo[] | undefined
  deleteItem: (task: AiAgentTask) => void
}) => {
  const orgId = useCurrentOrgId()
  const navigate = useNavigate()
  const router = useRouter()

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [hovered, setHovered] = useState(false)

  const projectKey = props.task.projectKey
  const projectName = props.task.projectName

  const task = Navigator.task(orgId, projectKey, props.task.id)
  const projectLocation = useProjectLocation(projectKey)

  const mostRecentPr = props.prs && props.prs.length > 0 ? props.prs[props.prs.length - 1] : null

  const CardComponent =
    mostRecentPr?.state?.toLowerCase() === "merged"
      ? props.task.successful
        ? TaskViewSceneStyles.MergedSuccessfulItemCard
        : TaskViewSceneStyles.MergedItemCard
      : props.task.successful || mostRecentPr?.state?.toLowerCase() === "open"
        ? TaskViewSceneStyles.SuccessfulItemCard
        : TaskViewSceneStyles.ItemCard

  return (
    <CardComponent
      onClick={() => navigate(task)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="task-card"
    >
      <Vertical gap="8px">
        <FirstLine>
          <ItemLink
            {...task}
            onClick={e => e.stopPropagation()}
            viewTransition
            data-testid="task-card-title"
          >
            {formatTaskTitle(props.task.title) || <em>Untitled</em>}
          </ItemLink>

          <Spring />

          {props.task.successful && (
            <Tooltip content="Marked as successful">
              <Heart20Green />
            </Tooltip>
          )}

          {mostRecentPr && mostRecentPr.state != "closed" && (
            <GitHubPrBadge pr={mostRecentPr} />
          )}
        </FirstLine>

        <SecondLine>
          <Tooltip content={`Project: ${projectName ?? normalizeProjectKey(projectKey)}`}>
            <ProjectLinkContainer>
              <ProjectLink {...projectLocation} onClick={e => e.stopPropagation()} viewTransition>
                {projectName ?? normalizeProjectKey(projectKey)}
              </ProjectLink>
            </ProjectLinkContainer>
          </Tooltip>

          <Spring />

          <CreatedAt visible={!hovered}>
            {formatTime(props.task.createdAt)}
          </CreatedAt>

          <HoverActions visible={hovered}>
            <Tooltip content="Copy link">
              <Button tabIndex={hovered ? undefined : -1} kind="icon" onClick={(e) => {
                e.stopPropagation()
                void copyToClipboard(window.location.host + router.buildLocation(task).href)
              }}>
                <LinkLight />
              </Button>
            </Tooltip>
            <ConfirmDialog
              open={confirmDialogOpen}
              onOpenChange={setConfirmDialogOpen}
              trigger={
                <Tooltip content="Delete">
                  <Button tabIndex={hovered ? undefined : -1} kind="icon" onClick={(e) => {
                    e.stopPropagation()
                    setConfirmDialogOpen(true)
                  }}><DeleteLight /></Button>
                </Tooltip>
              }
              description="Are you sure you want to delete this idea?"
              confirmText="Delete"
              onConfirm={() => {
                props.deleteItem(props.task)
                setConfirmDialogOpen(false)
              }}
            />
          </HoverActions>
        </SecondLine>
      </Vertical>
    </CardComponent>
  )
}

const PageTitle = styled(HomeStyles.ListTitle)`
  display: flex;
  align-items: center;
  gap: 8px;

  color: var(--text-color);
`

const UserProfileTitle = styled(Heading2)`
  color: var(--secondary-text-color);
  font-weight: 600;
  text-align: center;
  view-transition-name: user-tasks-title;

  ${ellipsis};
`

const SuccessfulToggle = styled(Link)`
  display: flex;
  align-items: center;
  align-self: center;

  view-transition-name: successful-toggle;
`

const SuccessfulActive = css`
  background: var(--text-color-5);
`

const UserTaskCountsContainer = styled(Vertical)`
  padding: 0 8px;

  view-transition-name: user-task-counts;
`

const UserTaskList = styled(Vertical)`
  gap: 16px;
  view-transition-name: user-tasks;
`

const ItemLink = styled(Link)`
  font-size: 18px;
  font-weight: 500;
  color: var(--text-color);

  &:hover {
    color: var(--text-color);
  }

  ${ellipsis};
`

const ProjectLinkContainer = styled(EllipsisContainer)`
  max-width: calc(100% - 80px);
`

const ProjectLink = styled(Link)`
  color: var(--secondary-text-color);
  font-weight: 600;

  &:hover {
    color: var(--text-color);
  }

  ${ellipsis};
`

const FirstLine = styled(Horizontal)`
  align-items: center;
  gap: 8px;

  line-height: 24px;
`

const SecondLine = styled(Horizontal)`
  align-items: center;
  gap: 4px;

  position: relative;

  color: var(--secondary-text-color);

  ${textStyles.smallFontSize};
`

const CreatedAt = styled(Ellipsis)<{ visible: boolean }>`
  position: absolute;
  right: 0;

  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? "0" : "-8px"});
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;

  line-height: 24px;
`

const HoverActions = styled(Horizontal)<{ visible: boolean }>`
  position: absolute;
  right: 0;

  align-items: center;
  gap: 16px;

  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? "0" : "8px"});
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
`

const FadeOutGradient = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;

  transform: translateX(-50%);

  width: calc(100% - (${PAGE_HORIZONTAL_PADDING} * 2));

  @media ${mobileBreakpoint} {
    width: calc(100% - (${PAGE_HORIZONTAL_PADDING_MOBILE} * 2));
  }

  height: 80px;

  background: linear-gradient(180deg, var(--background-color-0), var(--background-color) 100%);
  pointer-events: none;
`

